import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchPageBySlug, fetchGalleryBySlug } from '../lib/cms';
import Metadata from '../components/Metadata';
import GalleryView from '../components/GalleryView';

export default function ServiceDetail() {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // Fetch page content
        const page = await fetchPageBySlug(serviceSlug);
        if (!page) {
          // If service page doesn't exist, redirect to 404
          navigate('/404', { replace: true });
          return;
        }
        setPageData(page);

        // Fetch corresponding gallery if it exists, otherwise fallback to totalservice gallery
        let gallery = await fetchGalleryBySlug(serviceSlug);
        if (!gallery) {
          gallery = await fetchGalleryBySlug('totalservice');
        }
        setGalleryData(gallery);
      } catch (error) {
        console.error('Error loading service detail:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [serviceSlug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[--color-brand-cream]">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-[--color-brand-green-dark]"></i>
          <p className="font-sans text-sm text-[--color-brand-muted] font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  const title = pageData?.meta_title || `${pageData?.title || 'Ydelse'} - BP Totalservice v/Børge Hovmann Poulsen`;
  const description = pageData?.meta_description || "Professionel service udført af BP Totalservice.";

  const getOptimizedImageUrl = (url, width = 800) => {
    if (!url) return '';
    if (url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    return url;
  };

  return (
    <>
      <Metadata title={title} description={description} />

      {/* Header Section */}
      <section className="relative bg-[--color-brand-cream] overflow-hidden py-16 lg:py-24 px-6 flex items-center border-b border-[--color-brand-sand]">
        {/* Diagonal Sage Backdrop Split (Hero Left) */}
        <div 
          className="absolute inset-y-0 left-0 w-full lg:w-3/5 -skew-x-12 origin-top-left -translate-x-12 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, rgba(141, 164, 126, 0.35) 0%, rgba(76, 110, 59, 0.15) 100%)' }}
        ></div>

        {/* The "Peeking" Circle (Bottom-Left) */}
        <div 
          className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full z-10 pointer-events-none"
          style={{ backgroundColor: 'rgba(45, 75, 28, 0.15)' }}
        ></div>

        {/* Organic Background Arch (Top Right) */}
        <div 
          className="absolute top-0 right-0 w-[30%] h-[50%] rounded-bl-[150px] z-10 pointer-events-none"
          style={{ backgroundColor: 'rgba(141, 164, 126, 0.12)' }}
        ></div>

        <div className="relative z-20 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Editorial Headings */}
          <div className="lg:col-span-7 space-y-6">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 font-sans font-bold tracking-widest text-xs uppercase text-[--color-brand-green-dark] hover:text-[--color-brand-green-medium] transition-colors"
            >
              <i className="fa-solid fa-arrow-left text-[10px]"></i>
              Vores Ydelser
            </Link>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl text-[--color-brand-charcoal] font-bold leading-tight">
              {pageData?.title}
            </h1>
            <p className="font-sans text-base sm:text-lg text-[--color-brand-muted] max-w-xl leading-relaxed">
              {pageData?.subtitle || 'Professionel service udført med fokus på dine behov og høj kvalitet.'}
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="/kontakt"
                className="group inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-white text-[--color-brand-green-dark] border border-[--color-brand-green-dark]/20 hover:bg-white hover:text-[--color-brand-green-medium] hover:border-[--color-brand-green-medium] transition-all duration-300 text-sm font-semibold tracking-wide shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-right ml-2.5 text-xs transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {/* Right Column: Beautiful Leaf-shaped Image */}
          <div className="lg:col-span-5 relative flex flex-col items-center justify-center w-full max-w-md mx-auto overflow-visible">
            {/* Matrix Dot Pattern */}
            <div className="absolute -top-4 -right-4 grid grid-cols-6 gap-3 w-28 opacity-30 text-[--color-brand-green-dark] pointer-events-none z-0">
              {Array.from({ length: 24 }).map((_, i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full bg-current"></span>
              ))}
            </div>

            {/* Leaf Image Container */}
            <div className="relative w-full aspect-[4/3] sm:aspect-[1.2] rounded-tl-[100px] rounded-br-[100px] rounded-tr-3xl rounded-bl-3xl overflow-hidden shadow-xl border-4 border-white z-10 bg-[--color-brand-sage-pale]/20">
              {pageData?.images && pageData.images.length > 0 ? (
                <img
                  src={getOptimizedImageUrl(pageData.images[0], 800)}
                  alt={pageData.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[--color-brand-sage-pale]/30">
                  <i className="fa-solid fa-tree text-5xl text-[--color-brand-green-light]"></i>
                </div>
              )}
            </div>

            {/* Decorative Leaf Element */}
            <div className="absolute -bottom-10 -left-10 w-24 h-24 pointer-events-none z-20 opacity-40">
              <img 
                src="https://assets.auxodev.dk/assets/9d491525-9d2e-415f-8dca-b2b6aa0afab4/leaf-decor.png" 
                alt="Blad dekoration" 
                className="w-full h-full object-contain rotate-45"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pt-10 pb-20 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Contact Info */}
            <div className="lg:col-span-4 space-y-6 order-last lg:order-first">
              {/* Quick Contact Box */}
              <div className="bg-[--color-brand-cream] p-6 rounded-3xl border border-[--color-brand-sand] space-y-4">
                <h4 className="font-display text-lg font-bold text-[--color-brand-charcoal]">
                  Brug for hjælp?
                </h4>
                <p className="font-sans text-xs text-[--color-brand-muted] leading-relaxed">
                  Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig på dette område.
                </p>
                <div className="space-y-2 pt-2">
                  <a
                    href="tel:30441611"
                    className="flex items-center gap-2 text-sm font-bold text-[--color-brand-green-dark] hover:text-[--color-brand-green-medium] transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-xs"></i>
                    30 44 16 11
                  </a>
                  <a
                    href="mailto:post@bp-haveservice.dk"
                    className="flex items-center gap-2 text-sm font-bold text-[--color-brand-green-dark] hover:text-[--color-brand-green-medium] transition-colors whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-xs"></i>
                    post@bp-haveservice.dk
                  </a>
                </div>
              </div>

              {/* Back to Services Link */}
              <Link
                to="/ydelser"
                className="inline-flex items-center gap-2 font-sans text-xs font-bold text-[--color-brand-muted] hover:text-[--color-brand-green-dark] transition-colors uppercase tracking-wider"
              >
                <i className="fa-solid fa-arrow-left"></i>
                Se alle ydelser
              </Link>
            </div>

            {/* Right Column: Content & Gallery */}
            <div className="lg:col-span-8 space-y-12 order-first lg:order-last">
              {pageData?.content && (
                <div
                  className="prose-content"
                  dangerouslySetInnerHTML={{ __html: pageData.content }}
                />
              )}

              {/* Dynamic Gallery for this Service */}
              {galleryData && galleryData.images && galleryData.images.length > 0 && (
                <div className="pt-8 border-t border-[--color-brand-sand] space-y-6">
                  <h3 className="font-display text-xl md:text-2xl font-bold text-[--color-brand-charcoal]">
                    Billeder af {pageData?.title.toLowerCase()}
                  </h3>
                  <p className="font-sans text-sm text-[--color-brand-muted] -mt-2">
                    Her kan du få et indblik i, hvad vi kan hjælpe dig med på dette område.
                  </p>
                  <GalleryView images={galleryData.images} title={galleryData.title} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
